<template>
    <ul class="offer-item-categories-list" v-if="offer.hasItemCategories" :class="{ 'custom-color': useCustomColor }">
        <li
            v-for="category in offer.itemCategories"
            :key="category.categoryId"
            :class="{ active: isActiveCategory(category) }"
            @click="onClick(category)"
            @touchstart="onTouchStart"
            @touchend="(e) => onTouchEnd(e, category)"
        >
            <img
                v-if="showOfferCategoryImages && category.squareImagePath"
                :src="category.squareImagePath"
                class="category-image"
                alt=""
            />
            <span class="category-name">{{ translateByLocationLanguage(category.name) }}</span>
        </li>
    </ul>
</template>

<script setup lang="ts">
    import { useTapRecognizer } from "@/components/composables/useTapRecognizer";
    import type { ICustomerCategory } from "@/data/model/Customer";
    import { useOfferStore } from "@/data/stores/offer";
    import { translateByLocationLanguage } from "@/shared/localization/language";

    const offer = useOfferStore();
    defineProps<{
        useCustomColor?: boolean;
        showOfferCategoryImages: boolean;
    }>();

    const emit = defineEmits(["categorySelected"]);

    function isActiveCategory(category: ICustomerCategory) {
        return offer.activeCategory === category;
    }

    const { onTouchStart, onTouchEnd } = useTapRecognizer<ICustomerCategory>({
        onTap: (category) => setAsActiveCategory(category),
        toleranceY: 40,
    });

    function onClick(category: ICustomerCategory) {
        console.log("onClick", category);
        setAsActiveCategory(category);
    }

    function setAsActiveCategory(category: ICustomerCategory) {
        offer.setActiveCategory(category);
        emit("categorySelected", category);
    }
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    // kiosk: a little larger, the em based paddings below follow this
    .kiosk-mode ul.offer-item-categories-list {
        li {
            // same as the item name on the kiosk item tile
            font-size: 18px * variables.$kioskScale;

            > .category-image {
                width: 42px * variables.$kioskScale;
                height: 42px * variables.$kioskScale;
            }
        }
    }

    ul.offer-item-categories-list {
        list-style: none;
        margin: 0;
        padding: 0;

        li {
            display: flex;
            align-items: center;
            gap: 0.6em;
            font-size: 1.2em;
            font-weight: 500;
            padding: 1.25em 0.9em;
            color: colors.$fontColor;
            word-break: break-word;

            border-bottom: 1px solid #ccc;
            cursor: pointer;

            transition: background-color 0.15s linear;

            > .category-image {
                flex: 0 0 auto;
                width: 42px;
                height: 42px;
                object-fit: cover;
            }

            @include mixins.hover-and-active() {
                background-color: #ddd;
            }

            &.active {
                background-color: #777;
                color: white;
            }
        }

        &.custom-color {
            li {
                @include mixins.hover-and-active() {
                    background-color: var(--colorPrimaryHover);
                    color: white;
                }

                &.active {
                    background-color: var(--colorPrimary);
                    color: white;
                }
            }
        }
    }
</style>
